import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
  useToast,
  IconButton,
} from '@chakra-ui/react';
import { Linkedin, Twitter, Mail } from 'lucide-react';
import { SectionContainer } from '../SectionContainer';
import { ScrollReveal } from '../ScrollReveal';
import { Wordmark } from '../Wordmark';
import { trackEvent } from '../../utils/analytics';
import { useJoin } from '../../context/JoinContext';

const CONTACT_EMAIL = 'contact@agents.yetzira.ai';

const footerNav = [
  { label: 'How it works', href: '#how-it-works' },
  { label: 'The Engine', href: '#exponential' },
  { label: 'The Moat', href: '#moat' },
  { label: 'Learning', href: '#learning' },
  { label: 'Vision', href: '#vision' },
];

export default function FooterSection() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { openJoin } = useJoin();

  const handleJoin = () => {
    trackEvent('cta_click', { cta: 'join', source: 'footer' });
    openJoin();
  };

  const handleEmailCapture = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: 'Please enter a valid email.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'bottom',
      });
      return;
    }
    setLoading(true);
    trackEvent('email_capture', { email });
    await new Promise((r) => setTimeout(r, 800));
    setLoading(false);
    setEmail('');
    toast({
      title: "You're on the list.",
      description: "We'll be in touch.",
      status: 'success',
      duration: 4000,
      isClosable: true,
      position: 'bottom',
    });
  };

  const handleNavClick = (href) => {
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box as="footer" bg="bg.900" id="footer" role="contentinfo">
      {/* Closing CTA band */}
      <Box borderTop="1px solid" borderColor="whiteAlpha.100" py={{ base: '64px', md: '96px' }}>
        <Box maxW="1200px" mx="auto" px={{ base: '24px', md: '48px' }}>
          <ScrollReveal>
            <VStack spacing={8} align="center" textAlign="center">
              <Text
                fontFamily="'Space Grotesk', sans-serif"
                fontWeight="700"
                fontSize={{ base: 'clamp(1.6rem, 5vw, 2rem)', md: 'clamp(2rem, 4vw, 2.6rem)' }}
                letterSpacing="-0.025em"
                color="text.primary"
                maxW="700px"
                lineHeight="1.2"
              >
                The next organization won't be hired.{' '}
                <Box as="span" color="brand.400">
                  It will be created.
                </Box>
              </Text>

              <Flex gap={4} flexWrap="wrap" justify="center" w="100%">
                <Button
                  variant="gold"
                  size="lg"
                  px={8}
                  w={{ base: '100%', sm: 'auto' }}
                  onClick={handleJoin}
                  aria-label="Join Yetzira and create a new organization"
                >
                  Join us
                </Button>
                <Button
                  variant="ghost"
                  color="text.muted"
                  size="lg"
                  px={8}
                  w={{ base: '100%', sm: 'auto' }}
                  as="a"
                  href={`mailto:${CONTACT_EMAIL}`}
                  _hover={{ color: 'brand.400', bg: 'transparent' }}
                  onClick={() => trackEvent('cta_click', { cta: 'contact', source: 'footer' })}
                >
                  Contact us
                </Button>
              </Flex>
            </VStack>
          </ScrollReveal>
        </Box>
      </Box>

      {/* Footer bar */}
      <Box borderTop="1px solid" borderColor="whiteAlpha.100" py="40px" bg="bg.900">
        <Box maxW="1200px" mx="auto" px={{ base: '24px', md: '48px' }}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'flex-start', md: 'flex-start' }}
            gap={8}
          >
            {/* Left: Wordmark + tagline + social */}
            <VStack align="flex-start" spacing={4} maxW="240px">
              <Wordmark />
              <Text fontSize="0.85rem" color="text.muted" lineHeight="1.5">
                Build fully autonomous AI organizations that grow exponentially.
              </Text>
              <HStack spacing={2}>
                <IconButton
                  as="a"
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<Linkedin size={16} />}
                  variant="ghost"
                  size="sm"
                  color="text.muted"
                  aria-label="LinkedIn"
                  _hover={{ color: 'brand.400', bg: 'transparent' }}
                />
                <IconButton
                  as="a"
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<Twitter size={16} />}
                  variant="ghost"
                  size="sm"
                  color="text.muted"
                  aria-label="Twitter / X"
                  _hover={{ color: 'brand.400', bg: 'transparent' }}
                />
                <IconButton
                  as="a"
                  href={`mailto:${CONTACT_EMAIL}`}
                  icon={<Mail size={16} />}
                  variant="ghost"
                  size="sm"
                  color="text.muted"
                  aria-label="Email us"
                  _hover={{ color: 'brand.400', bg: 'transparent' }}
                />
              </HStack>
            </VStack>

            {/* Middle: Nav */}
            <VStack align="flex-start" spacing={3}>
              <Text
                fontFamily="'JetBrains Mono', monospace"
                fontSize="0.72rem"
                textTransform="uppercase"
                letterSpacing="1px"
                color="brand.300"
                mb={1}
              >
                Navigate
              </Text>
              <Flex as="nav" aria-label="Footer navigation" direction="column" gap={2}>
                {footerNav.map((link) => (
                  <Text
                    key={link.label}
                    as="a"
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    fontSize="0.9rem"
                    color="text.muted"
                    cursor="pointer"
                    _hover={{ color: 'brand.400' }}
                    transition="color 0.2s"
                  >
                    {link.label}
                  </Text>
                ))}
              </Flex>
            </VStack>

            {/* Right: Email capture */}
            <Box w={{ base: '100%', md: '300px' }}>
              <Text
                fontFamily="'JetBrains Mono', monospace"
                fontSize="0.72rem"
                textTransform="uppercase"
                letterSpacing="1px"
                color="brand.300"
                mb={2}
              >
                Stay informed
              </Text>
              <Text fontSize="0.85rem" color="text.muted" mb={3} lineHeight="1.5">
                Get updates on yetzira.ai progress and early access.
              </Text>
              <form onSubmit={handleEmailCapture} aria-label="Email subscription">
                <InputGroup size="md">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    bg="bg.700"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    color="text.primary"
                    _placeholder={{ color: 'text.muted' }}
                    _focus={{
                      borderColor: 'brand.500',
                      boxShadow: '0 0 0 1px rgba(99,102,241,0.4)',
                    }}
                    borderRadius="10px"
                    pr="5rem"
                    aria-label="Email address for updates"
                  />
                  <InputRightElement w="4.5rem" h="100%">
                    <Button
                      type="submit"
                      size="sm"
                      variant="gold"
                      h="32px"
                      px={3}
                      fontSize="0.8rem"
                      isLoading={loading}
                      borderRadius="7px"
                      mr="4px"
                    >
                      Join
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </form>
            </Box>
          </Flex>

          {/* Bottom bar */}
          <Divider borderColor="whiteAlpha.100" mt={8} mb={5} />
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            justify="space-between"
            align={{ base: 'flex-start', sm: 'center' }}
            gap={3}
          >
            <Text fontSize="0.8rem" color="text.muted">
              © {new Date().getFullYear()} yetzira.ai. All rights reserved.
            </Text>
            <HStack spacing={5}>
              <Text as="a" href="#" fontSize="0.8rem" color="text.muted" _hover={{ color: 'brand.400' }} transition="color 0.2s">
                Privacy
              </Text>
              <Text as="a" href="#" fontSize="0.8rem" color="text.muted" _hover={{ color: 'brand.400' }} transition="color 0.2s">
                Terms
              </Text>
              <Text
                as="a"
                href={`mailto:${CONTACT_EMAIL}`}
                fontSize="0.8rem"
                color="text.muted"
                _hover={{ color: 'brand.400' }}
                transition="color 0.2s"
              >
                Contact
              </Text>
            </HStack>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}
